/**
 * @fileoverview Table components for rendering tabular data with scrolling functionality.
 * This file provides components for rendering tables with horizontal scrolling and pagination indicators.
 * @see {/src/hooks/useScrollInTable.ts}
 * @see {/src/utils/stylesForTable.ts}
 * @see {/src/components/ui/tables/TableRender.tsx}
 * @active
 */

import { useEffect, useRef, useState } from "react";

import {
  Box,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  TableColumnHeaderProps,
} from "@chakra-ui/react";

import EaySvg from "@/assets/svg/eay-icon.svg";
import { useScrollInTable } from "@/hooks/useScrollInTable";
import {
  stylesForBullet,
  stylesForEayTab,
  stylesForTable,
} from "@/utils/stylesForTable";

/**
 * @interface MaskItem
 * @description Interface for mask items used in table columns
 * @active
 */
interface MaskItem {
  title: string;
  icon?: any; // Using any for icon type to avoid external dependency
}

/**
 * @interface RenderTabPeopleProps
 * @description Props for RenderTabPeople component
 * @active
 */
interface RenderTabPeopleProps {
  mask: MaskItem[];
  data: any[];
  tabContainerStyle?: Record<string, any>;
  setIsVisibleEay?: (isVisible: boolean) => void;
  blockSwitch?: ({
    item,
    mainItem,
    data,
  }: {
    item: any;
    mainItem: any;
    data: any[];
  }) => React.ReactNode;
  isFundInvestments?: boolean;
  isShadow?: boolean;
  lastPersonIndexArr?: number[];
  setIsShadow?: (isShadow: boolean) => void;
  isPeoplePage?: boolean;
}

/**
 * @function RenderTabPeople
 * @description Renders a table component with support for horizontal scrolling and pagination
 * @param {RenderTabPeopleProps} props - Component props
 * @returns {JSX.Element} Rendered table component
 * @active
 */
export function RenderTabPeople({
  mask,
  data,
  tabContainerStyle,
  setIsVisibleEay,
  blockSwitch,
  isFundInvestments,
  isShadow,
  lastPersonIndexArr = [],
  setIsShadow = () => {},
  isPeoplePage = false,
}: RenderTabPeopleProps): JSX.Element {
  const refScrollBox = useRef<HTMLDivElement>(null);
  const refTable = useRef<HTMLTableElement>(null);

  const { currentBullet, bulletArr, isScroll } = useScrollInTable({
    refScrollBox,
    refTable,
    mask,
  });

  useEffect(() => {
    setIsShadow(isScroll);
  }, [isScroll, setIsShadow]);

  useEffect(() => {
    if (bulletArr && bulletArr.length > 1 && setIsVisibleEay) {
      setIsVisibleEay(true);
    } else if (setIsVisibleEay) {
      setIsVisibleEay(false);
    }
  }, [bulletArr, setIsVisibleEay]);

  const switchTdStyles = ({
    mainItem,
    index,
    dataIndex,
  }: {
    mainItem: any;
    index: number;
    dataIndex: number;
  }) => {
    if (mainItem?.tdStatus === "wide" && !index && mask?.length === 1) {
      return stylesForTable.TbodyTrTdName;
    }
    if (mainItem?.tdStatus === "wide" && mask?.length > 1) {
      return stylesForTable.TbodyTrTdEmpty;
    }
    if (
      !mainItem?.tdStatus &&
      !index &&
      mask?.length === 1 &&
      isFundInvestments
    ) {
      return stylesForTable.TbodyTrTdFirst;
    }

    if (
      !lastPersonIndexArr.includes(dataIndex) &&
      (!isPeoplePage
        ? mainItem?.person?.nameFull
        : mainItem?.resEntity?.nameBrand)
    ) {
      return { ...stylesForTable.TbodyTrTd, border: "none" };
    }

    return stylesForTable.TbodyTrTd;
  };

  return (
    <Flex
      flexDir={"column"}
      overflow={mask?.length === 1 ? "unset" : "hidden"}
      minW={tabContainerStyle?.w || "unset"}
      w={mask?.length === 1 ? "fit-content" : "100%"}
    >
      <Flex
        {...tabContainerStyle}
        ref={refScrollBox}
        className={mask?.length > 1 ? "hide-scrollbar" : ""}
        position={"relative"}
        _before={{
          content: '""',
          position: "absolute",
          top: "0",
          right: "0",
          width: "4px",
          height: "100%",
          background:
            "linear-gradient(90deg, #0A2540 -12.5%, rgba(255, 255, 255, 0.00) 75%)",
          zIndex: "1",
          transition: "all 0.2s ease-in-out",
          opacity: isShadow ? "1" : "0",
        }}
      >
        <Table variant="unstyled" ref={refTable}>
          <Thead>
            <Tr>
              {mask?.map((item, index) => {
                const shouldAddPr =
                  index === mask?.length - 1 && mask?.length > 1;
                return (
                  <Th
                    key={item?.title + index}
                    pl={index === 0 && mask?.length === 1 ? "0px" : "6px"}
                    {...(stylesForTable.TheadTrTh as TableColumnHeaderProps)}
                    pr={shouldAddPr ? "8px" : undefined}
                    isTruncated
                    h={"31px"}
                    textAlign={item?.icon ? "center" : "start"}
                  >
                    {item?.icon ? (
                      <Icon as={item?.icon} w={"14px"} h={"14px"} />
                    ) : null}
                    {!item?.icon ? <Text as={"h3"}>{item?.title}</Text> : null}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {data?.map((mainItem, dataIndex) => {
              return (
                <Tr key={`${mainItem?.id}-${dataIndex}`}>
                  {mask?.map((item, index) => {
                    const shouldAddPr =
                      index === mask?.length - 1 && mask?.length > 1;
                    return (
                      <Td
                        key={`${item?.title}-${index}`}
                        isTruncated
                        pl={index === 0 && mask?.length === 1 ? "0px" : "6px"}
                        {...switchTdStyles({ mainItem, index, dataIndex })}
                        pr={shouldAddPr ? "8px" : undefined}
                      >
                        <Flex
                          align={"center"}
                          gap={"8px"}
                          w={{
                            base: mask?.length === 1 ? "154px" : "fit-content",
                            md: "100%",
                          }}
                          whiteSpace={"nowrap"}
                          overflow={"hidden"}
                          textOverflow={"ellipsis"}
                          sx={{
                            maskImage: {
                              base:
                                mask?.length === 1
                                  ? "linear-gradient(to left, transparent 5%, white 25%)"
                                  : "",
                              md: "linear-gradient(to left, transparent 0%, white 0%)",
                            },
                          }}
                          maxW={{
                            base: mask?.length === 1 ? "150px" : "100%",
                            md: "100%",
                          }}
                        >
                          {blockSwitch && typeof blockSwitch === "function"
                            ? blockSwitch({ item, mainItem, data })
                            : null}
                        </Flex>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Flex>
      {mask?.length > 1 && bulletArr && bulletArr.length > 1 && (
        <Flex
          alignSelf={"center"}
          gap={"4px"}
          pos={"absolute"}
          bottom={"-14px"}
          left={"50%"}
          transform={"translate(-50%, 0)"}
        >
          {bulletArr.map((item, index) => {
            return (
              <Box
                key={"circle" + index}
                borderColor={currentBullet === index ? "mainBlue" : "grey"}
                bgColor={currentBullet === index ? "mainBlue" : "transparent"}
                {...stylesForBullet.Box}
              ></Box>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
}

/**
 * @interface EayTabPeopleProps
 * @description Props for EayTabPeople component
 * @active
 */
interface EayTabPeopleProps {
  data: any[];
  tabContainerStyle?: Record<string, any>;
  ModalComponent: React.ComponentType<{ control: any; data: any }>;
  lastPersonIndexArr?: number[];
}

/**
 * @function EayTabPeople
 * @description Renders a secondary table component with modal interaction
 * @param {EayTabPeopleProps} props - Component props
 * @returns {JSX.Element} Rendered table component
 * @active
 */
export function EayTabPeople({
  data,
  tabContainerStyle,
  ModalComponent,
  lastPersonIndexArr = [],
}: EayTabPeopleProps): JSX.Element {
  const controlModal = useDisclosure();
  const [choiceData, setChoiceData] = useState<any>(null);

  const switchStyled = ({ item, index }: { item: any; index: number }) => {
    if (item?.tdStatus) {
      return stylesForEayTab.TbodyTrTdEmpty;
    }
    if (item?.person && !lastPersonIndexArr.includes(index)) {
      return { ...stylesForEayTab.TbodyTrTd, border: "none" };
    }
    return stylesForEayTab.TbodyTrTd;
  };
  return (
    <Box {...tabContainerStyle}>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th {...stylesForEayTab.TheadTrTh} h={"31px"}>
              <Flex h={"17px"}> </Flex>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {data?.map((item, index) => {
            const firstItem =
              item?.person?.nameFull && Array.isArray(data)
                ? data.find(
                    (prsn) => prsn?.person?.nameFull === item?.person?.nameFull
                  )
                : item;

            return (
              <Tr key={index + "row"}>
                <Td {...switchStyled({ item, index })}>
                  <Flex
                    cursor={"pointer"}
                    onClick={() => {
                      setChoiceData(item);
                      controlModal.onOpen();
                    }}
                  >
                    {!item?.tdStatus && item?.id === firstItem?.id ? (
                      <EaySvg />
                    ) : (
                      <></>
                    )}
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {/* Only render ModalComponent when choiceData is not null */}
      {choiceData && (
        <ModalComponent {...{ control: controlModal, data: choiceData }} />
      )}
    </Box>
  );
}
